import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../../app";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { AuthenticatedAssetLoader } from "../../../services/authenticated-asset-loader";
import { DefaultEventService } from "../../../services/event-service";
import { DialogSceneParams } from "../../../shared/domain";
import { InventoryDTO } from "../../../shared/dtos";
import { Endpoints, getAssetUrl, SceneId } from "../../../shared/enum";
import { InventoryMenu } from "../../menu/inventory-menu";

const { lazyInject } = getDecorators(container);

export class InventoryScene extends Phaser.Scene {
    private menu: InventoryMenu;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    private callingScene: Phaser.Scene;

    constructor() {
        super({ key: SceneId.INVENTORY_SCENE });
    }

    preload() {
        // Items spritesheet loaded in loadAuthenticatedAssets() via authenticated proxy
    }

    init(params: DialogSceneParams) {
        // listen for keyboard events
        this.callingScene = params.scene;
        this.events.on("wake", (data) => this.onWake(data));
        this.sys.events.on("sleep", () => this.scene.get(SimpleGame.CURRENT_MAP_ID).scene.wake());
        this.scene.bringToTop();
    }

    public onWake(data) {
        this.scene.bringToTop();
    }

    public create(sceneId: string) {
        // Load authenticated inventory data
        this.loadAuthenticatedAssets()
            .then(() => {
                const inventory = this.cache.json.get("inventory") as InventoryDTO;
                this.menu = new InventoryMenu(0, 0, 0, 0, inventory, this, 4);
                this.menu.draw();
            })
            .catch((error) => {
                console.error("Failed to load inventory:", error);
            });
    }

    private async loadAuthenticatedAssets(): Promise<void> {
        const url = `${Endpoints.BASE_URL}/${Endpoints.INVENTORY}/${SimpleGame.CURRENT_GAME}`;
        await this.assetLoader.loadJson(this, "inventory", url);

        const inventory = this.cache.json.get("inventory") as InventoryDTO;
        const seen = new Set<string>();
        for (const inventoryItem of inventory.items ?? []) {
            const spriteData = inventoryItem.item_definition?.sprite_data;
            if (!spriteData?.online_image || !spriteData.image_path) continue;
            const key = "items/" + spriteData.image_path;
            if (seen.has(key) || this.textures.exists(key)) continue;
            seen.add(key);
            const blobUrl = await this.assetLoader.fetchAsBlob(getAssetUrl(spriteData.online_image));
            await new Promise<void>((resolve) => {
                this.load.spritesheet(key, blobUrl, {
                    frameWidth: spriteData.width || 32,
                    frameHeight: spriteData.height || 32,
                } as Phaser.Types.Loader.FileTypes.ImageFrameConfig);
                this.load.once("complete", resolve);
                this.load.start();
            });
        }
    }
}
